import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWizardMode } from '@/contexts/WizardModeContext';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Save, Loader2, Bell, TrendingUp, Clock, Users, Check } from 'lucide-react';
import { toast } from 'sonner';
import { fetchNotificationSettings, saveNotificationSettings } from '@/lib/data-service';
import type { NotificationSettingsData } from '@/lib/data-service';
import { appendAuditEntry } from '@/lib/audit-log-storage';
import { useWipThresholds } from '@/hooks/use-wip-thresholds';
import { useDataMode } from '@/contexts/DataModeContext';
import { useAuth } from '@/providers/AuthProvider';
import { SettingsLayout } from '@/components/Pages/SettingsLayout';
import { cn } from '@/lib/utils';

const ALL_DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
] as const;

// ─── Sub-panel for billing schedule options ─────────────────────────────────
interface ScheduleSubPanelProps {
  selectedDays: Set<string>;
  onToggleDay: (day: string) => void;
  timeValue: string;
  onTimeChange: (value: string) => void;
}

function ScheduleSubPanel({ selectedDays, onToggleDay, timeValue, onTimeChange }: ScheduleSubPanelProps) {
  return (
    <div className="mt-4 rounded-md border border-border bg-muted/30 pl-4 pr-4 pt-4 pb-4 border-l-[3px] border-l-primary/40 space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium">Send on days</Label>
        <div className="flex flex-wrap gap-2">
          {ALL_DAYS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => onToggleDay(key)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
                selectedDays.has(key)
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-input hover:bg-accent',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="billing-trigger-time" className="text-sm font-medium">
          Send at time (UTC)
        </Label>
        <Input
          id="billing-trigger-time"
          type="time"
          value={timeValue}
          onChange={(e) => onTimeChange(e.target.value)}
          className="w-32"
        />
      </div>
    </div>
  );
}

// ─── Sub-panel for role unfilled delay ─────────────────────────────────────
interface DelaySubPanelProps {
  delayHours: number;
  onDelayChange: (hours: number) => void;
}

function DelaySubPanel({ delayHours, onDelayChange }: DelaySubPanelProps) {
  return (
    <div className="mt-4 rounded-md border border-border bg-muted/30 pl-4 pr-4 pt-4 pb-4 border-l-[3px] border-l-primary/40 space-y-2">
      <Label htmlFor="role-unfilled-delay" className="text-sm font-medium">
        Delay before notifying
      </Label>
      <p className="text-xs text-muted-foreground">
        Only notify if the required role has been unfilled for longer than this duration. Set to 0 to notify immediately.
      </p>
      <div className="flex items-center gap-2">
        <Input
          id="role-unfilled-delay"
          type="number"
          min={0}
          step={1}
          value={delayHours}
          onChange={(e) => onDelayChange(Math.max(0, parseInt(e.target.value, 10) || 0))}
          className="w-24"
        />
        <span className="text-sm text-muted-foreground">hours</span>
      </div>
    </div>
  );
}

// ─── Sub-panel for Forecast EAC threshold ──────────────────────────────────
interface ForecastEacSubPanelProps {
  threshold: number;
  onThresholdChange: (value: number) => void;
}

function ForecastEacSubPanel({ threshold, onThresholdChange }: ForecastEacSubPanelProps) {
  return (
    <div className="mt-4 rounded-md border border-border bg-muted/30 pl-4 pr-4 pt-4 pb-4 border-l-[3px] border-l-primary/40 space-y-2">
      <div className="flex items-center flex-wrap gap-2 text-sm">
        <span className="text-muted-foreground">Alert when EAC exceeds budget by more than</span>
        <Input
          type="number"
          min={0}
          step={1}
          value={threshold}
          onChange={(e) => onThresholdChange(Math.max(0, parseInt(e.target.value, 10) || 0))}
          className="w-20 h-7 text-sm px-2"
        />
        <span className="text-muted-foreground">%</span>
      </div>
      <p className="text-xs text-muted-foreground">
        Set to 0 to alert on any overrun. Set to 10 to alert only when EAC is more than 10% over budget.
      </p>
    </div>
  );
}

// ─── Individual notification row ───────────────────────────────────────────
interface NotificationItemProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children?: React.ReactNode;
}

function NotificationItem({ id, label, description, checked, onCheckedChange, children }: NotificationItemProps) {
  return (
    <div className="py-4">
      <div className="flex gap-4">
        <div className="flex-shrink-0 pt-0.5">
          <Checkbox id={id} checked={checked} onCheckedChange={onCheckedChange} />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <Label htmlFor={id} className="text-sm font-semibold leading-snug cursor-pointer">
            {label}
          </Label>
          <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

// ─── Collapsible accordion section ─────────────────────────────────────────
interface AccordionSectionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function AccordionSection({ icon, title, description, defaultOpen = true, children }: AccordionSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-4 hover:bg-accent/40 transition-colors text-left group"
        aria-expanded={open}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-snug">{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
        </div>
        <svg
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
            open && 'rotate-180',
          )}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="border-t border-border px-4 py-2 divide-y divide-border">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────
export function SettingsNotificationsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isWizardMode, registerSave } = useWizardMode();

  const { isDemoMode } = useDataMode();
  const { authenticatedUser } = useAuth();
  const { thresholds: wipThresholds } = useWipThresholds();
  const changedBy = isDemoMode
    ? 'Demo User'
    : authenticatedUser?.name ?? authenticatedUser?.email ?? 'Unknown User';

  const defaultSettings: NotificationSettingsData = {
    projectAssignmentEnabled: true,
    projectOverBudgetEnabled: true,
    billingTriggerDateEnabled: true,
    billingTriggerDateDays: 'mon,tue,wed,thu,fri',
    billingTriggerDateTimeUtc: '06:00',
    fixedPriceBillingPercentEnabled: true,
    hoursOverBudgetEnabled: true,
    salesUnderBudgetEnabled: true,
    projectHealthChangedEnabled: true,
    projectEndDateApproachingEnabled: true,
    invoiceOverdueEnabled: true,
    scheduleOverrunEnabled: true,
    agedWipAlertEnabled: true,
    projectStatusChangedEnabled: true,
    projectRoleUnfilledEnabled: false,
    projectRoleUnfilledDelayHours: 0,
  };

  const [settings, setSettings] = useState<NotificationSettingsData>(defaultSettings);
  const [savedSettings, setSavedSettings] = useState<NotificationSettingsData>(defaultSettings);

  // ── Forecast EAC & Roll-Forward notification state (mock — no API) ────────
  const mockForecastDefaults = {
    forecastEacOverBudgetPmEnabled: false,
    forecastEacOverBudgetPmThreshold: 0,
    forecastEacOverBudgetReviewerEnabled: false,
    forecastEacOverBudgetReviewerThreshold: 0,
    rollForwardReadyReviewerEnabled: true,
    rollForwardReadyPmEnabled: true,
    rollForwardChallengesReviewerEnabled: true,
  };
  const [forecastSettings, setForecastSettings] = useState(mockForecastDefaults);
  const [savedForecastSettings, setSavedForecastSettings] = useState(mockForecastDefaults);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  const hasUnsavedChanges =
    settings.projectAssignmentEnabled !== savedSettings.projectAssignmentEnabled ||
    settings.projectOverBudgetEnabled !== savedSettings.projectOverBudgetEnabled ||
    settings.billingTriggerDateEnabled !== savedSettings.billingTriggerDateEnabled ||
    settings.billingTriggerDateDays !== savedSettings.billingTriggerDateDays ||
    settings.billingTriggerDateTimeUtc !== savedSettings.billingTriggerDateTimeUtc ||
    settings.fixedPriceBillingPercentEnabled !== savedSettings.fixedPriceBillingPercentEnabled ||
    settings.hoursOverBudgetEnabled !== savedSettings.hoursOverBudgetEnabled ||
    settings.salesUnderBudgetEnabled !== savedSettings.salesUnderBudgetEnabled ||
    settings.projectHealthChangedEnabled !== savedSettings.projectHealthChangedEnabled ||
    settings.projectEndDateApproachingEnabled !== savedSettings.projectEndDateApproachingEnabled ||
    settings.invoiceOverdueEnabled !== savedSettings.invoiceOverdueEnabled ||
    settings.scheduleOverrunEnabled !== savedSettings.scheduleOverrunEnabled ||
    settings.agedWipAlertEnabled !== savedSettings.agedWipAlertEnabled ||
    settings.projectStatusChangedEnabled !== savedSettings.projectStatusChangedEnabled ||
    settings.projectRoleUnfilledEnabled !== savedSettings.projectRoleUnfilledEnabled ||
    settings.projectRoleUnfilledDelayHours !== savedSettings.projectRoleUnfilledDelayHours ||
    forecastSettings.forecastEacOverBudgetPmEnabled !== savedForecastSettings.forecastEacOverBudgetPmEnabled ||
    forecastSettings.forecastEacOverBudgetPmThreshold !== savedForecastSettings.forecastEacOverBudgetPmThreshold ||
    forecastSettings.forecastEacOverBudgetReviewerEnabled !== savedForecastSettings.forecastEacOverBudgetReviewerEnabled ||
    forecastSettings.forecastEacOverBudgetReviewerThreshold !== savedForecastSettings.forecastEacOverBudgetReviewerThreshold ||
    forecastSettings.rollForwardReadyReviewerEnabled !== savedForecastSettings.rollForwardReadyReviewerEnabled ||
    forecastSettings.rollForwardReadyPmEnabled !== savedForecastSettings.rollForwardReadyPmEnabled ||
    forecastSettings.rollForwardChallengesReviewerEnabled !== savedForecastSettings.rollForwardChallengesReviewerEnabled;

  const selectedDays = new Set(settings.billingTriggerDateDays.split(',').filter(Boolean));

  const toggleDay = (day: string) => {
    const next = new Set(selectedDays);
    if (next.has(day)) {
      if (next.size > 1) next.delete(day);
    } else {
      next.add(day);
    }
    const ordered = ALL_DAYS.map((d) => d.key).filter((k) => next.has(k));
    setSettings((prev) => ({ ...prev, billingTriggerDateDays: ordered.join(',') }));
  };

  useEffect(() => {
    async function loadSettings() {
      try {
        const data = await fetchNotificationSettings();
        setSettings(data);
        setSavedSettings(data);
      } catch (error) {
        console.error('Failed to load notification settings:', error);
        toast.error('Failed to load notification settings.');
      } finally {
        setIsLoading(false);
      }
    }
    loadSettings();
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveNotificationSettings(settings);
      const changes: string[] = [];

      if (settings.projectAssignmentEnabled !== savedSettings.projectAssignmentEnabled)
        changes.push(`Project Assignment: ${settings.projectAssignmentEnabled ? 'On' : 'Off'}`);
      if (settings.projectOverBudgetEnabled !== savedSettings.projectOverBudgetEnabled)
        changes.push(`Cost Over Budget: ${settings.projectOverBudgetEnabled ? 'On' : 'Off'}`);
      if (settings.hoursOverBudgetEnabled !== savedSettings.hoursOverBudgetEnabled)
        changes.push(`Hours Budget Critical: ${settings.hoursOverBudgetEnabled ? 'On' : 'Off'}`);
      if (settings.salesUnderBudgetEnabled !== savedSettings.salesUnderBudgetEnabled)
        changes.push(`Sales Under Budget: ${settings.salesUnderBudgetEnabled ? 'On' : 'Off'}`);
      if (settings.projectHealthChangedEnabled !== savedSettings.projectHealthChangedEnabled)
        changes.push(`Project Health Changed: ${settings.projectHealthChangedEnabled ? 'On' : 'Off'}`);
      if (settings.projectEndDateApproachingEnabled !== savedSettings.projectEndDateApproachingEnabled)
        changes.push(`Project End Date Approaching: ${settings.projectEndDateApproachingEnabled ? 'On' : 'Off'}`);
      if (settings.invoiceOverdueEnabled !== savedSettings.invoiceOverdueEnabled)
        changes.push(`Invoice Overdue: ${settings.invoiceOverdueEnabled ? 'On' : 'Off'}`);
      if (settings.scheduleOverrunEnabled !== savedSettings.scheduleOverrunEnabled)
        changes.push(`Schedule Overrun Detected: ${settings.scheduleOverrunEnabled ? 'On' : 'Off'}`);
      if (settings.agedWipAlertEnabled !== savedSettings.agedWipAlertEnabled)
        changes.push(`Aged WIP Alert: ${settings.agedWipAlertEnabled ? 'On' : 'Off'}`);
      if (settings.projectStatusChangedEnabled !== savedSettings.projectStatusChangedEnabled)
        changes.push(`Project Status Changed: ${settings.projectStatusChangedEnabled ? 'On' : 'Off'}`);
      if (settings.projectRoleUnfilledEnabled !== savedSettings.projectRoleUnfilledEnabled)
        changes.push(`Project Role Unfilled: ${settings.projectRoleUnfilledEnabled ? 'On' : 'Off'}`);
      if (settings.projectRoleUnfilledDelayHours !== savedSettings.projectRoleUnfilledDelayHours)
        changes.push(`Role Unfilled Delay: ${settings.projectRoleUnfilledDelayHours}h`);
      if (settings.billingTriggerDateEnabled !== savedSettings.billingTriggerDateEnabled)
        changes.push(`Billing Trigger: ${settings.billingTriggerDateEnabled ? 'On' : 'Off'}`);
      if (settings.fixedPriceBillingPercentEnabled !== savedSettings.fixedPriceBillingPercentEnabled)
        changes.push(`Fixed Price Billing %: ${settings.fixedPriceBillingPercentEnabled ? 'On' : 'Off'}`);
      if (settings.billingTriggerDateDays !== savedSettings.billingTriggerDateDays)
        changes.push(`Billing days: ${settings.billingTriggerDateDays}`);
      if (settings.billingTriggerDateTimeUtc !== savedSettings.billingTriggerDateTimeUtc)
        changes.push(`Billing time: ${settings.billingTriggerDateTimeUtc}`);

      appendAuditEntry({
        changedBy,
        category: 'Settings',
        action: 'Updated',
        entityName: 'Notification Settings',
        newValue: changes.join('; ') || 'No visible changes',
        detail: 'Updated via Settings > Notifications',
      });
      setSavedSettings({ ...settings });
      setSavedForecastSettings({ ...forecastSettings });
      console.log('[Forecast Notification Settings]', forecastSettings);
      if (!isWizardMode) {
        toast.success('Notification settings saved successfully.');
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 2000);
      }
    } catch (error) {
      console.error('Failed to save notification settings:', error);
      toast.error('Failed to save notification settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [settings, savedSettings, changedBy, isWizardMode]);

  // Register save with wizard so the Next button triggers handleSave.
  const wizardSaveRef = useRef(handleSave);
  wizardSaveRef.current = handleSave;
  useState(() => { if (isWizardMode) registerSave(async () => { await wizardSaveRef.current(); }); });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <SettingsLayout currentPath={location.pathname} onNavigate={navigate}>
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
        <div className="space-y-6 p-6 pb-10" data-tour="settings-notifications-triggers">

          {/* ── Budget Notifications ── */}
          <AccordionSection
            icon={<TrendingUp className="h-5 w-5 text-primary" />}
            title="Budget Notifications"
            description="Notify Project Managers when cost, hours, or sales budgets reach critical thresholds"
            defaultOpen={true}
          >
            {/* Cost Over Budget */}
            <NotificationItem
              id="project-over-budget"
              label="Cost Over Budget"
              description="Notify the Project Manager when a project's total cost spend (actual + committed) reaches or exceeds 100% of its cost budget"
              checked={settings.projectOverBudgetEnabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({ ...prev, projectOverBudgetEnabled: checked }))
              }
            />

            {/* Hours Budget Critical */}
            <NotificationItem
              id="hours-over-budget"
              label="Hours Budget Critical"
              description="Notify the Project Manager when hours burned reach 90% (warning) or 100%+ (exceeded) of the budgeted hours — includes project completion % for context"
              checked={settings.hoursOverBudgetEnabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({ ...prev, hoursOverBudgetEnabled: checked }))
              }
            />

            {/* Sales Under Budget */}
            <NotificationItem
              id="sales-under-budget"
              label="Sales Under Budget"
              description="Notify the Project Manager when a project's actual or forecast sales revenue falls below the sales budget"
              checked={settings.salesUnderBudgetEnabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({ ...prev, salesUnderBudgetEnabled: checked }))
              }
            />

            {/* Forecast EAC Over Budget — Project Manager */}
            <NotificationItem
              id="forecast-eac-over-budget-pm"
              label="Forecast EAC Over Budget — Project Manager"
              description="Notify the Project Manager when the Estimate at Completion (EAC) exceeds the total cost budget."
              checked={forecastSettings.forecastEacOverBudgetPmEnabled}
              onCheckedChange={(checked) =>
                setForecastSettings((prev) => ({ ...prev, forecastEacOverBudgetPmEnabled: checked }))
              }
            >
              {forecastSettings.forecastEacOverBudgetPmEnabled && (
                <ForecastEacSubPanel
                  threshold={forecastSettings.forecastEacOverBudgetPmThreshold}
                  onThresholdChange={(value) =>
                    setForecastSettings((prev) => ({ ...prev, forecastEacOverBudgetPmThreshold: value }))
                  }
                />
              )}
            </NotificationItem>

            {/* Forecast EAC Over Budget — Forecast Reviewer */}
            <NotificationItem
              id="forecast-eac-over-budget-reviewer"
              label="Forecast EAC Over Budget — Forecast Reviewer"
              description="Notify all assigned Forecast Reviewers when the Estimate at Completion (EAC) exceeds the total cost budget."
              checked={forecastSettings.forecastEacOverBudgetReviewerEnabled}
              onCheckedChange={(checked) =>
                setForecastSettings((prev) => ({ ...prev, forecastEacOverBudgetReviewerEnabled: checked }))
              }
            >
              {forecastSettings.forecastEacOverBudgetReviewerEnabled && (
                <ForecastEacSubPanel
                  threshold={forecastSettings.forecastEacOverBudgetReviewerThreshold}
                  onThresholdChange={(value) =>
                    setForecastSettings((prev) => ({ ...prev, forecastEacOverBudgetReviewerThreshold: value }))
                  }
                />
              )}
            </NotificationItem>
          </AccordionSection>

          {/* ── Project Health & Schedule ── */}
          <AccordionSection
            icon={<Bell className="h-5 w-5 text-primary" />}
            title="Project Health & Schedule"
            description="Notify Project Managers about health status changes and schedule overruns"
            defaultOpen={true}
          >
            {/* Project Health Changed */}
            <NotificationItem
              id="project-health-changed"
              label="Project Health Changed"
              description="Notify the Project Manager when a project's computed health status changes between Green, Amber, and Red — covers both degradation and improvement"
              checked={settings.projectHealthChangedEnabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({ ...prev, projectHealthChangedEnabled: checked }))
              }
            />

            {/* Project End Date Approaching */}
            <NotificationItem
              id="project-end-date-approaching"
              label="Project End Date Approaching"
              description="Notify the Project Manager when a project's end date is exactly 30 days away — includes current project status for context"
              checked={settings.projectEndDateApproachingEnabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({ ...prev, projectEndDateApproachingEnabled: checked }))
              }
            />

            {/* Schedule Overrun Detected */}
            <NotificationItem
              id="schedule-overrun"
              label="Schedule Overrun Detected"
              description="Notify the Project Manager when a project's forecast end date becomes later than its planned end date — overrun is expressed in weeks"
              checked={settings.scheduleOverrunEnabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({ ...prev, scheduleOverrunEnabled: checked }))
              }
            />
          </AccordionSection>

          {/* ── Billing & Finance ── */}
          <AccordionSection
            icon={<Clock className="h-5 w-5 text-primary" />}
            title="Billing & Finance"
            description="Notify Project Managers about billing milestones, overdue invoices, and aged WIP"
            defaultOpen={true}
          >
            {/* Billing Trigger Date */}
            <NotificationItem
              id="billing-trigger-date"
              label="Billing Trigger Date"
              description="Notify the Project Manager when a billing milestone trigger date has passed and an invoice needs to be generated"
              checked={settings.billingTriggerDateEnabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({ ...prev, billingTriggerDateEnabled: checked }))
              }
            >
              {settings.billingTriggerDateEnabled && (
                <ScheduleSubPanel
                  selectedDays={selectedDays}
                  onToggleDay={toggleDay}
                  timeValue={settings.billingTriggerDateTimeUtc}
                  onTimeChange={(v) =>
                    setSettings((prev) => ({ ...prev, billingTriggerDateTimeUtc: v }))
                  }
                />
              )}
            </NotificationItem>

            {/* Fixed Price Billing by % */}
            <NotificationItem
              id="fixed-price-billing-percent"
              label="Fixed Price Billing by %"
              description="Notify the Project Manager when a fixed price project reaches the billing milestone trigger percentage and an invoice needs to be generated"
              checked={settings.fixedPriceBillingPercentEnabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({ ...prev, fixedPriceBillingPercentEnabled: checked }))
              }
            />

            {/* Invoice Overdue */}
            <NotificationItem
              id="invoice-overdue"
              label="Invoice Overdue"
              description="Notify the Project Manager on the day an invoice passes its due date without being marked as Paid — includes invoice reference and customer name"
              checked={settings.invoiceOverdueEnabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({ ...prev, invoiceOverdueEnabled: checked }))
              }
            />

            {/* Aged WIP Alert */}
            <NotificationItem
              id="aged-wip-alert"
              label="Aged WIP Alert"
              description={`Notify the Project Manager when a project has WIP aged over ${wipThresholds.agedWipDaysThreshold} days without being billed — includes WIP amount and last billed date`}
              checked={settings.agedWipAlertEnabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({ ...prev, agedWipAlertEnabled: checked }))
              }
            />

            {/* Roll-Forward Proposal Ready — Forecast Reviewer */}
            <NotificationItem
              id="roll-forward-ready-reviewer"
              label="Roll-Forward Proposal Ready — Forecast Reviewer"
              description="Notify Forecast Reviewers when a financial period has closed and a Roll-Forward proposal is ready to be submitted."
              checked={forecastSettings.rollForwardReadyReviewerEnabled}
              onCheckedChange={(checked) =>
                setForecastSettings((prev) => ({ ...prev, rollForwardReadyReviewerEnabled: checked }))
              }
            />

            {/* Roll-Forward Proposal Ready for Review — Project Manager */}
            <NotificationItem
              id="roll-forward-ready-pm"
              label="Roll-Forward Proposal Ready for Review — Project Manager"
              description="Notify the Project Manager when a Forecast Reviewer has submitted a Roll-Forward proposal and it is ready for their review."
              checked={forecastSettings.rollForwardReadyPmEnabled}
              onCheckedChange={(checked) =>
                setForecastSettings((prev) => ({ ...prev, rollForwardReadyPmEnabled: checked }))
              }
            />

            {/* Roll-Forward Challenges to Resolve — Forecast Reviewer */}
            <NotificationItem
              id="roll-forward-challenges-reviewer"
              label="Roll-Forward Challenges to Resolve — Forecast Reviewer"
              description="Notify Forecast Reviewers when a Project Manager has responded to a Roll-Forward proposal and there are challenges to resolve."
              checked={forecastSettings.rollForwardChallengesReviewerEnabled}
              onCheckedChange={(checked) =>
                setForecastSettings((prev) => ({ ...prev, rollForwardChallengesReviewerEnabled: checked }))
              }
            />
          </AccordionSection>

          {/* ── Project & Governance ── */}
          <AccordionSection
            icon={<Users className="h-5 w-5 text-primary" />}
            title="Project & Governance"
            description="Notify Project Managers, Sponsors, and Key People about project assignments, status changes, and unfilled roles"
            defaultOpen={true}
          >
            {/* Project Assignment */}
            <NotificationItem
              id="project-assignment"
              label="Project Assignment"
              description="Notify users when they are assigned as Project Manager for a project"
              checked={settings.projectAssignmentEnabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({ ...prev, projectAssignmentEnabled: checked }))
              }
            />

            {/* Project Status Changed */}
            <NotificationItem
              id="project-status-changed"
              label="Project Status Changed"
              description="Notify the Project Manager and Key People (Project Sponsor, Account Manager) when a project's status changes — includes who made the change"
              checked={settings.projectStatusChangedEnabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({ ...prev, projectStatusChangedEnabled: checked }))
              }
            />

            {/* Project Role Unfilled */}
            <NotificationItem
              id="project-role-unfilled"
              label="Project Role Unfilled"
              description="Notify the Project Manager and Project Sponsor when a required project role has no person assigned — a configurable delay prevents noise for roles filled shortly after project creation"
              checked={settings.projectRoleUnfilledEnabled}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({ ...prev, projectRoleUnfilledEnabled: checked }))
              }
            >
              {settings.projectRoleUnfilledEnabled && (
                <DelaySubPanel
                  delayHours={settings.projectRoleUnfilledDelayHours}
                  onDelayChange={(hours) =>
                    setSettings((prev) => ({ ...prev, projectRoleUnfilledDelayHours: hours }))
                  }
                />
              )}
            </NotificationItem>
          </AccordionSection>

        </div>
        </div>

        {/* Sticky footer save bar — hidden inside onboarding wizard (Next button saves instead) */}
        {!isWizardMode && (
          <div className="border-t bg-background px-6 py-3 flex items-center justify-end gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              disabled={isSaving || !hasUnsavedChanges}
              onClick={() => { setSettings(savedSettings); setForecastSettings(savedForecastSettings); }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasUnsavedChanges || isSaving}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : showSaveSuccess ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {showSaveSuccess ? 'Saved' : 'Save'}
            </Button>
          </div>
        )}
      </div>
    </SettingsLayout>
  );
}

export default SettingsNotificationsPage;
